import React, { useEffect, useRef, useState, useCallback } from 'react';
import ErrorBoundary from './ErrorBoundary';
import config from '../utils/config';

function AdUnit({ className = '', style = {}, slotType = 'default' }) {
  const adRef = useRef(null);
  const [adLoaded, setAdLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Get AdSense configuration from environment variables
  const adsenseConfig = {
    clientId: process.env.REACT_APP_GOOGLE_ADSENSE_CLIENT_ID || 'ca-pub-XXXXXXXXXXXXXXXXX',
    slotIds: {
      header: process.env.REACT_APP_GOOGLE_ADSENSE_SLOT_ID_HEADER || 'XXXXXXXXXX',
      myfiles: process.env.REACT_APP_GOOGLE_ADSENSE_SLOT_ID_MYFILES || 'XXXXXXXXXX', // SAFE: folder dashboard
      folder: process.env.REACT_APP_GOOGLE_ADSENSE_SLOT_ID_FOLDER || 'XXXXXXXXXX', // SAFE: folder overview page
      // REMOVED FOR ADSENSE COMPLIANCE: no ads near download content
      upload: process.env.REACT_APP_GOOGLE_ADSENSE_SLOT_ID_UPLOAD || 'XXXXXXXXXX',
      default: process.env.REACT_APP_GOOGLE_ADSENSE_SLOT_ID_HEADER || 'XXXXXXXXXX'
    }
  };

  const currentSlotId = adsenseConfig.slotIds[slotType] || adsenseConfig.slotIds.default;
  const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
  const shouldLoadAds = isProduction && config.features.googleAds && adsenseConfig.clientId !== 'ca-pub-XXXXXXXXXXXXXXXXX';

  // Debounced resize handler to prevent rapid ResizeObserver triggers
  const debouncedResize = useCallback(() => {
    let timeoutId;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth <= 768);
      }, 150);
    };
  }, []);

  useEffect(() => {
    // Detect mobile viewport
    setIsMobile(window.innerWidth <= 768);
    
    // Add debounced resize listener
    const handleResize = debouncedResize();
    window.addEventListener('resize', handleResize);

    // Load ads with enhanced mobile support and error handling
    const loadAd = async () => {
      try {
        // Wait longer on mobile to avoid layout thrashing
        const mobileDelay = window.innerWidth <= 768 ? 500 : 200;
        
        // Only load ads in production with valid configuration
        if (!shouldLoadAds) {
          console.log('AdSense disabled: Not in production or invalid configuration');
          return;
        }

        // Load Google AdSense script if not already loaded
        if (!window.adsbygoogle) {
          const script = document.createElement('script');
          script.async = true;
          script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adsenseConfig.clientId}`;
          script.crossOrigin = 'anonymous';
          
          // Wait for script to load
          await new Promise((resolve, reject) => {
            script.onload = () => setTimeout(resolve, 100); // Small delay after load
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }
        
        // Use double requestAnimationFrame for extra stability
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              try {
                if (window.adsbygoogle && adRef.current && adRef.current.isConnected) {
                  // Wrap in try-catch and suppress any ResizeObserver errors
                  const originalError = console.error;
                  console.error = (...args) => {
                    if (args[0] && args[0].toString().includes('ResizeObserver')) return;
                    originalError.apply(console, args);
                  };
                  
                  (window.adsbygoogle = window.adsbygoogle || []).push({});
                  setAdLoaded(true);
                  
                  // Restore console.error after a delay
                  setTimeout(() => {
                    console.error = originalError;
                  }, 1000);
                }
              } catch (pushError) {
                // Silently handle any push errors
                if (!pushError.message.includes('ResizeObserver')) {
                  console.warn('AdSense push error:', pushError);
                }
              }
            }, mobileDelay);
          });
        });
        
      } catch (error) {
        if (!error.message.includes('ResizeObserver')) {
          console.warn('AdSense script loading error:', error);
        }
      }
    };

    // Delay ad loading on mobile even more to prevent conflicts
    const loadDelay = window.innerWidth <= 768 ? 1000 : 300;
    const timeoutId = setTimeout(loadAd, loadDelay);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [debouncedResize]);

  // If ads feature is disabled, render nothing
  if (!config.features.googleAds) {
    return null;
  }

  return (
    <ErrorBoundary fallback={
      <div style={{
        width: '100%',
        height: isMobile ? '60px' : '90px',
        backgroundColor: '#1e293b',
        border: '1px solid #374151',
        borderRadius: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        color: '#6b7280',
        margin: isMobile ? '12px 0' : '16px 0'
      }}>
        Ad Space
      </div>
    }>
      <div 
        ref={adRef}
        className={`ad-unit ${className}`}
        style={{
          width: '100%',
          textAlign: 'center',
          margin: isMobile ? '12px 0' : '16px 0',
          minHeight: adLoaded ? 'auto' : (isMobile ? '60px' : '90px'),
          maxHeight: isMobile ? '250px' : '400px',
          contain: 'layout style size', // CSS containment to prevent layout thrashing
          position: 'relative',
          overflow: 'hidden',
          ...style
        }}
      >
        <ins
          className="adsbygoogle"
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            minHeight: isMobile ? '50px' : '60px',
            maxHeight: isMobile ? '200px' : '350px',
            contain: 'layout style size'
          }}
          data-ad-client={adsenseConfig.clientId}
          data-ad-slot={currentSlotId}
          data-ad-format={isMobile ? 'rectangle' : 'auto'}
          data-full-width-responsive="true"
        />
        {!adLoaded && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#1e293b',
            border: '1px solid #374151',
            borderRadius: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            color: '#6b7280',
            zIndex: 1
          }}>
            {isMobile ? 'Ad' : 'Advertisement'}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default AdUnit; 